import { createBrowserRouter } from 'react-router';
import { Skeleton } from '@orgus/ui';
import { AppShell } from './components/shell/AppShell.tsx';
import type { RouteHandle } from './components/shell/AppShell.tsx';
import Agents from './pages/Agents.tsx';
import Assistant from './pages/Assistant.tsx';
import Audit from './pages/Audit.tsx';
import Capacity from './pages/Capacity.tsx';
import Design from './pages/Design.tsx';
import Discipline from './pages/Discipline.tsx';
import GettingStarted from './pages/GettingStarted.tsx';
import Inbox from './pages/Inbox.tsx';
import Machines from './pages/Machines.tsx';
import { PhoneLayoutSwitch } from './features/mobile/PhoneLayoutSwitch.tsx';
import { routeSwitch } from './lib/routeSwitch.ts';
import Missions from './pages/Missions.tsx';
import NotFound from './pages/NotFound.tsx';
import Pair from './pages/Pair.tsx';
import Overview from './pages/Overview.tsx';
import Pipelines from './pages/Pipelines.tsx';
import Projects from './pages/Projects.tsx';
import Security from './pages/Security.tsx';
import Health from './pages/Health.tsx';
import Sessions from './pages/Sessions.tsx';
import Settings from './pages/Settings.tsx';
import Spend from './pages/Spend.tsx';
import Terminals from './pages/Terminals.tsx';
import Wall from './pages/Wall.tsx';

/** What /flows shows while its code loads (the editor is a separate chunk). */
function FlowsLoading() {
  return (
    <div className="flex h-full flex-col gap-3 p-6" role="status" aria-label="Loading flows">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-32 w-full rounded-card" />
    </div>
  );
}

/**
 * Routes. Each section page is ui/src/pages/<Name>.tsx with a default export.
 * Deep links use query params: /sessions?session=<id>, /projects?project=<id>, /machines?machine=<id>,
 * /pipelines?pipeline=<pipelineKey>, /pipelines/landing?queue=<queueId>&landing=<id>, /pipelines/environments?project=<id>.
 */
export const router = createBrowserRouter([
  // The phone layout loads on its own, so the console does not carry it.
  { path: '/m/*', lazy: async () => ({ Component: (await import('./features/mobile/MobileApp.tsx')).default }) },
  // One-tap links from email and Teams land here; the signed token is the permission, no sign-in.
  { path: '/act', lazy: async () => ({ Component: (await import('./features/notify/ActPage.tsx')).default }) },
  // The Orgus installer opens /pair/<id>#k=<code> to approve a computer (ADR 0042 decision 1). Loaded
  // with the app, not lazily, so the code is taken from the address bar before any sign-in redirect.
  { path: '/pair/:requestId', element: <Pair /> },
  // A join link opened in a browser (ADR 0090): what to do on the new computer, and the link as a QR
  // code. It redeems nothing and needs no sign-in. Not in the nav.
  { path: '/join/:ticket', lazy: async () => ({ Component: (await import('./pages/Join.tsx')).default }) },
  {
    path: '/',
    element: (
      <>
        <AppShell />
        <PhoneLayoutSwitch />
      </>
    ),
    children: [
      { index: true, element: <Overview /> },
      { path: 'missions/*', element: <Missions /> },
      { path: 'sessions', element: <Sessions /> },
      { path: 'agents', element: <Agents /> },
      { path: 'wall', element: <Wall />, handle: { title: 'Live wall' } satisfies RouteHandle },
      { path: 'flows/*', HydrateFallback: FlowsLoading, lazy: () => import('./pages/Flows.tsx').then((m) => ({ Component: m.default })) },
      { path: 'pipelines/*', element: <Pipelines /> },
      { path: 'projects', element: <Projects /> },
      { path: 'machines', element: <Machines /> },
      { path: 'capacity', element: <Capacity /> },
      { path: 'spend/*', element: <Spend /> },
      { path: 'audit', element: <Audit /> },
      { path: 'inbox', element: <Inbox /> },
      { path: 'assistant', element: <Assistant /> },
      { path: 'discipline', element: <Discipline /> },
      { path: 'terminals', element: <Terminals /> },
      { path: 'security', element: <Security /> },
      { path: 'health', element: <Health /> },
      { path: 'settings/*', element: <Settings /> },
      { path: 'getting-started', element: <GettingStarted />, handle: { title: 'Getting started' } satisfies RouteHandle },
      { path: 'design', element: <Design />, handle: { title: 'Design system' } satisfies RouteHandle },
      { path: '*', element: <NotFound />, handle: { title: 'Not found' } satisfies RouteHandle },
    ],
  },
]);

// A new page holds live updates until it commits (lib/routeSwitch.ts; AppShell ends the hold).
let lastPath = router.state.location.pathname;
router.subscribe((state) => {
  const path = state.location.pathname;
  if (path === lastPath) return;
  lastPath = path;
  routeSwitch.begin(path);
});
